import type { RouteLocationNormalizedLoaded } from 'vue-router';
import { useRoute } from 'vue-router';
import useQRouter from '~/composables/useQRouter';

import type { ComputedRef } from 'vue';
import {
  computed,
} from 'vue';

import {
  useStore,
} from 'vuex';

import {
  FILTER_KEYS, SUB_FILTER_KEYS, allowedQueryFilterKeyMap, commonFilterConfig, filterKeyToPathMap, filterSet,
} from '~/components/MyLibraryV2/Constants';

export interface IMyLibraryFilterParams {
  teamId?: string,
  collectionId?: string,
}
// need to make enum based
export interface IMyLibraryFilters extends IMyLibraryFilterParams {
  filterId: string,
  order: string,
  sortBy: string,
  contentType: string,
  quizStatus?: string,
  term?: string,
  cloned?: boolean,
}

function correctRoute() {
  const route = useRoute();
  const router = useQRouter();
  router.push('correctRoute', getCorrectedRoute(route));
}

function useMyLibraryFiltersFromUrl(): ComputedRef<IMyLibraryFilters> {
  const route = useRoute();
  const filters = computed(() => ({
    filterId: getFilterIdFromRoute(route),
    ...deriveCorrectedQueryFromRoute(route),
    ...deriveCorrectedParamsFromRoute(route),
  }));

  return filters;
}

function getCorrectedRoute(route: RouteLocationNormalizedLoaded) {
  const path = deriveCorrectedPathFromRoute(route);

  const correctedRoute = {
    path,
    query: route.query,
    params: route.params,
  };
  return correctedRoute;
}

function getPathFromFilterIdAndParams(filterId: string, params: IMyLibraryFilterParams = {}) {
  if (filterId === FILTER_KEYS.COLLECTIONS && params[SUB_FILTER_KEYS.COLLECTION_ID]) {
    return `${filterKeyToPathMap[FILTER_KEYS.COLLECTIONS]}/${params[SUB_FILTER_KEYS.COLLECTION_ID]}`;
  }
  if (filterId === FILTER_KEYS.TEAMS && params[SUB_FILTER_KEYS.TEAM_ID]) {
    return `${filterKeyToPathMap[FILTER_KEYS.TEAMS]}/${params[SUB_FILTER_KEYS.TEAM_ID]}`;
  }
  return filterKeyToPathMap[filterId];
}

function deriveCorrectedPathFromRoute(route: RouteLocationNormalizedLoaded) {
  const { params } = route;
  const filterId = getFilterIdFromRoute(route);
  return getPathFromFilterIdAndParams(filterId, params);
}

function deriveCorrectedParamsFromRoute(route: RouteLocationNormalizedLoaded) {
  const { params } = route;
  const filterId = getFilterIdFromRoute(route);
  if (filterId === FILTER_KEYS.COLLECTIONS) {
    return { collectionId: params.collectionId };
  }

  if (filterId === FILTER_KEYS.TEAMS) {
    return { teamId: params.teamId };
  }

  return {};
}

function deriveCorrectedQueryFromRoute(route: RouteLocationNormalizedLoaded): IMyLibraryFiltersQuery {
  const { query } = route;
  const filterId = getFilterIdFromRoute(route);
  const allowedFilterKeys = allowedQueryFilterKeyMap[filterId];
  const newQuery = {};

  allowedFilterKeys?.forEach((key: string) => {
    const valueFromRoute = query[key]?.toString() ?? '';
    // todo : test remove isCommonFilterConfigKey dependency
    const { default: defaultValue, options } = commonFilterConfig[key];
    newQuery[key] = options.length === 0 || options.includes(valueFromRoute) ? valueFromRoute : defaultValue;
  });

  return newQuery;
}

function getFilterIdFromRoute({ path }: RouteLocationNormalizedLoaded) {
  const regex = /\/admin\/my-library\/([^/]+)/;
  let filterId = '';
  const matches = path.match(regex);
  if (matches && matches.length > 1) {
    filterId = matches[1];
  }
  return filterSet.has(filterId) ? filterId : FILTER_KEYS.CREATED_BY_ME;
}

export function deriveRouteQueryFromFilters(filters: IMyLibraryFilters): IMyLibraryFiltersQuery {
  const { filterId, ...filterParams } = filters;
  const allowedFilterKeys = allowedQueryFilterKeyMap[filterId];
  const newQuery = {};
  allowedFilterKeys?.forEach((key: string) => {
    const valueFromRoute = filterParams[key]?.toString() ?? '';
    // todo : test remove isCommonFilterConfigKey dependency
    const { default: defaultValue, options } = commonFilterConfig[key];
    newQuery[key] = options.length === 0 || options.includes(valueFromRoute) ? valueFromRoute : defaultValue;
  });
  return newQuery;
}

export function deriveRouteParamsFromFilters(filters: IMyLibraryFilters) {
  const { filterId, ...filterParams } = filters;

  if (filterId === FILTER_KEYS.COLLECTIONS) {
    return { collectionId: filterParams.collectionId };
  }

  if (filterId === FILTER_KEYS.TEAMS) {
    return { teamId: filterParams.teamId };
  }

  return {};
}

function updateURL(router, { filterId, query = {}, params = {} }: IMyLibraryFilters) {
  // figure out `router` being undefined here
  router.push('updateURL', {
    path: getPathFromFilterIdAndParams(filterId, params),
    query,
    params,
  });
}

function useSetFilters() {
  // router is getting undefined usage || investigate
  const router = useQRouter();
  const currentFilters = useMyLibraryFiltersFromUrl();

  const setFiltersById = (filterId: string) => updateURLByFilterId(router, currentFilters.value, filterId);
  const setFiltersByQuery = (query: IMyLibraryFiltersQuery) => updateURLByQuery(router, currentFilters.value, query);
  const setFiltersByFilterIdAndParams = (
    filterId: string,
    params: IMyLibraryFilterParams = {},
  ) => updateURLByFilterIdAndParams(router, currentFilters.value, filterId, params);

  return { setFiltersById, setFiltersByQuery, setFiltersByFilterIdAndParams };
}

function updateURLByFilterId(router, currentFilters: IMyLibraryFilters, newFilterId: string) {
  const query = deriveRouteQueryFromFilters({ ...currentFilters, filterId: newFilterId });
  updateURL(router, { filterId: newFilterId, query });
}

/**
 * TODO: merge and simplify - updateURLByQuery and updateURLByFilterIdAndParams
 */

function updateURLByQuery(router, currentFilters: IMyLibraryFilters, updatedQuery: IMyLibraryFiltersQuery) {
  const { filterId } = currentFilters;
  const query = deriveRouteQueryFromFilters({ ...currentFilters, ...updatedQuery });
  const params = deriveRouteParamsFromFilters({
    ...currentFilters,
    filterId,
  });
  updateURL(router, { filterId, query, params });
}

function updateURLByFilterIdAndParams(
  router,
  currentFilters: IMyLibraryFilters,
  newFilterId: string,
  newParams: IMyLibraryFilterParams = {},
) {
  const query = deriveRouteQueryFromFilters({ ...currentFilters, filterId: newFilterId });

  const params = deriveRouteParamsFromFilters({
    ...currentFilters,
    ...newParams,
    filterId: newFilterId,
  });

  updateURL(router, { filterId: newFilterId, params, query });
}

function useLibraryLimitsFeatureFlag() {
  const store = useStore();

  const featureFlags = computed(() => store?.getters['root/featureFlags']);

  const myLibraryV2 = computed(() => featureFlags.value?.['my-library-v2']?.defaultValue);

  const libraryLimits = computed(() => featureFlags.value?.['library-limits']?.defaultValue);

  const showLibraryLimits = computed(() => myLibraryV2.value && libraryLimits.value);

  return showLibraryLimits;
}

export default useMyLibraryFiltersFromUrl;

export {
  updateURL,
  useSetFilters,
  correctRoute,
  useLibraryLimitsFeatureFlag,
};
